import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useAddSellValue } from "../../../../hooks/sellValues/useAddSellValue";
import { useModifySellValue } from "../../../../hooks/sellValues/useModifySellValue";
import { useDeleteSellValue } from "../../../../hooks/sellValues/useDeleteSellValue";
import { ISellValue } from "../../../../hooks/sellValues";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { ModifyFreeTextAttributeDialog } from "../../ModifyFreeTextAttributeDialog";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifySellValueDialog: FunctionComponent<IModifySellValueDialogProps> = ({
  open,
  onClose,
  sellValue,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = sellValue ? strings.sell_value_title : strings.msv_new_title;
  const { addSellValue } = useAddSellValue();
  const { modifySellValue } = useModifySellValue();
  const { deleteSellValue } = useDeleteSellValue();
  const [isLoading, setIsLoading] = useState(false);
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    group: Yup.string().required(strings.msv_missing_error),
    dollars: Yup.number().typeError(strings.not_number_error).required(strings.msv_missing_error)
  });
  const formik = useFormik({
    initialValues: {
      group: sellValue?.group ?? "",
      dollars: sellValue?.dollars ?? 0,
      description: sellValue?.description ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ group, dollars, description }) => {
      setIsLoading(true);
      if (sellValue) {
        const { status, body } = await modifySellValue({
          uuid: sellValue.uuid,
          group: group,
          dollars: dollars,
          description: description
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_sellvalue_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addSellValue({
          group: group,
          dollars: dollars,
          description: description
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_sellvalue_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!sellValue) return;
    setIsLoading(true);
    const { status, body } = await deleteSellValue({ uuid: sellValue.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(sellValue);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {sellValue && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.msv_group_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="group"
              name="group"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.msv_group_placeholder}
              value={formik.values.group}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.msv_dollars_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="dollars"
              name="dollars"
              size="small"
              placeholder={strings.msv_dollars_placeholder}
              value={formik.values.dollars}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.msv_description_title}</FieldTitle>
          <Card className={styles.profileCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.SELL_VALUES}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifySellValueDialogProps {
  open: boolean;
  onClose: () => void;
  sellValue?: ISellValue;
  onNewItem?: (item: ISellValue) => void;
  onModifiedItem?: (item: ISellValue) => void;
  onDeletedItem?: (item: ISellValue) => void;
}
